import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import { useMemo } from 'react';
import { FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useBookingContext } from '../../../contexts/BookingProvider';
import useAppointmentActions from '../../../hooks/APIHooks/appointments/useAppointmentActions';
import useAppointmentRequestActions from '../../../hooks/APIHooks/appointments/useAppointmentRequestActions';
import useClientActions from '../../../hooks/APIHooks/users/useClientActions';
import { AppointmentReqBody } from '../../../types/appointmentRequests';
import { AvailableAssociate } from '../../../types/appointments';
import {
  convertNumberToLocaleString,
  showFullName,
  showPicture,
} from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import {
  calculateTimeSlotDurationInMinutes,
  convertTimeSlotInto24HrsTime,
  showDurationInHoursMinutes,
} from '../../../utils/timeAndDate';
import AcademiaRating from '../AcademiaRating';
import AcademiaSectionWrapper from '../AcademiaSectionWrapper';
import CardBadge from '../Badges/CardBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AssociateCardCount from '../Cards/AssociateCardCount';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const SlotBookingConsentModal = ({
  associate,
  handleModalClose,
  handleAdditionalModalClose,
  slotDetails,
  slotCount,
}: {
  associate: AvailableAssociate;
  handleModalClose: () => void;
  handleAdditionalModalClose: () => void;
  slotDetails: any;
  slotCount: number;
}) => {
  // destructuring the associate object here
  const {
    id,
    first_name,
    middle_name,
    last_name,
    profile_picture,
    username,
    slot_duration,
    total_appointments,
    total_languages,
    feedback_count,
    associate_rating,
    credits_per_appointment,
    language_names,
  } = associate || {};

  // integration of context hooks here
  const { searchDate, serviceId } = useBookingContext();
  const { currentUser, currentUserType } = useAuthContext();

  // integration of custom API hooks here
  const { clientListHandler } = useClientActions('clientListHandler');
  const { createNewAppointmentRequest } = useAppointmentRequestActions('');
  const { getAppointmentsByClientIdHandler } = useAppointmentActions(
    '',
    undefined,
    currentUser?.id
  );

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  console.log('SLOT DETAILS: ', slotDetails);

  // splitting the time slot here
  const { start_time, end_time } = convertTimeSlotInto24HrsTime(
    slotDetails.time_slot
  );

  // calculating whole slot duration here
  const totalSlotDuration = calculateTimeSlotDurationInMinutes(
    start_time,
    end_time
  );

  const clientOptions = useMemo(() => {
    if (!clientListHandler.data?.is_success) return [];

    return clientListHandler.data.data.map((client) => ({
      id: client.id,
      name: client.client_full_name,
    }));
  }, [clientListHandler.data]);

  // handler function to handle booking appointment feature declared here
  const bookAppointmentHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      let body: AppointmentReqBody = {
        associate: id,
        client:
          currentUserType === 'client'
            ? (currentUser?.id as number)
            : values.client.id,
        service: serviceId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        date: searchDate,
        start_time: start_time,
        end_time: end_time,
        want_reminder: slotDetails.want_reminder,
        additional_info: values.additional_info,
        created_by: currentUser?.username,
      };

      // binding optional fields here
      if (slotDetails.want_reminder) {
        body = { ...body, reminder_minutes: slotDetails.reminder_time };
      }

      console.log('APPOINTMENT REQUEST CREATE BODY: ', body);

      await createNewAppointmentRequest
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Appointment Booked!!');

            handleModalClose();
            handleAdditionalModalClose();
            queryClient.invalidateQueries({
              queryKey: ['appointment-request-list'],
            });

            if (currentUserType === 'client') {
              queryClient.invalidateQueries({
                queryKey: ['appointment-by-client-id', currentUser?.id],
              });

              queryClient.invalidateQueries({
                queryKey: ['current-client-details', currentUser?.id],
              });
            } else {
              queryClient.invalidateQueries({
                queryKey: ['appointment-by-associate-id', currentUser?.id],
              });
            }
            getAppointmentsByClientIdHandler.refetch();
          } else {
            toast.error(res.message || 'Appointment Booking Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering slot booking consent modal here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[700px]'>
      <ModalTitle titleText='booking summary' />

      <div>
        <AcademiaSectionWrapper sectionTitle='Associate Info'>
          <div className='flex flex-col md:flex-row items-center justify-between'>
            {profile_picture ? (
              <div className='size-24 rounded-full overflow-hidden'>
                <img
                  className='h-full w-full object-cover'
                  src={showPicture(profile_picture)}
                  alt={`avatar of ${username}`}
                />
              </div>
            ) : (
              <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
                <FaUser className='size-14 text-gray-300' />
              </div>
            )}

            <div className='hidden md:block w-1/2'>
              <div className='grid grid-cols-3'>
                <AssociateCardCount
                  count={total_appointments}
                  text='Appointments'
                />
                <AssociateCardCount count={feedback_count} text='Reviews' />
                <AssociateCardCount count={total_languages} text='Languages' />
              </div>
            </div>

            <div className='block md:hidden'>
              <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                {showFullName(first_name, middle_name, last_name)}
              </h3>

              <AcademiaRating
                size={30}
                ratingValue={associate_rating}
                isReadonly
              />
            </div>
          </div>

          <div className='flex flex-col md:flex-row items-center justify-between mt-3 md:mt-0'>
            <div className='hidden md:block'>
              <h3 className='text-lg text-base-dark-blue font-medium mt-1'>
                {showFullName(first_name, middle_name, last_name)}
              </h3>

              <AcademiaRating
                size={30}
                ratingValue={associate_rating}
                isReadonly
              />
            </div>

            <div className='w-full md:w-1/2'>
              <p className='text-xl text-center font-medium text-base-dark-green'>
                {credits_per_appointment} credits
                <span className='text-sm font-normal text-base-orange'>
                  /slot
                </span>
              </p>

              <div className='flex flex-wrap items-center justify-center gap-2 mt-2'>
                {language_names.map((language, index) => (
                  <CardBadge key={index} badgeText={language} />
                ))}
              </div>
            </div>

            <div className='block md:hidden w-full mt-3'>
              <div className='grid grid-cols-3'>
                <AssociateCardCount
                  count={total_appointments}
                  text='Appointments'
                />
                <AssociateCardCount count={feedback_count} text='Reviews' />
                <AssociateCardCount count={total_languages} text='Languages' />
              </div>
            </div>
          </div>
        </AcademiaSectionWrapper>

        <AcademiaSectionWrapper sectionTitle='Appointment Info'>
          <div className='space-y-2 md:space-y-1'>
            <div className='flex flex-col md:flex-row items-center gap-1 text-base-dark-green'>
              <p className='w-[150px]'>Appointment Date</p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>
                {moment(searchDate).format('DD-MM-YYYY')}
              </p>
            </div>

            <div className='flex flex-col md:flex-row items-center gap-1 text-base-purple'>
              <p className='w-[150px]'>Appointment Time</p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>{slotDetails.time_slot} </p>
              <span className='text-sm text-base-cyan'>
                [{slotCount} {slotCount > 1 ? 'slots' : 'slot'} in total]
              </span>
            </div>

            <div className='flex flex-col md:flex-row items-center gap-1 text-base-light-blue'>
              <p className='w-[150px]'>Slot Duration</p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>
                {showDurationInHoursMinutes(totalSlotDuration)}
              </p>
            </div>

            <div className='flex flex-col md:flex-row items-center gap-1 text-base-orange'>
              <p className='w-[150px] text-center md:text-left'>Total Cost</p>
              <p className='font-medium hidden md:block'>:</p>
              <p className='font-medium'>
                {convertNumberToLocaleString(
                  totalSlotDuration * (credits_per_appointment / slot_duration)
                )}{' '}
                Credits
              </p>
            </div>
          </div>
        </AcademiaSectionWrapper>
      </div>

      <div className='!mt-8'>
        <ModalTitle titleText='are you sure?' />

        <p className='text-left whitespace-pre-wrap text-base-dark-blue'>
          Are you sure you want to book this appointment?
        </p>

        <Formik
          initialValues={{
            client: SELECT_FIELD_DEFAULT_VALUE,
            additional_info: '',
          }}
          onSubmit={bookAppointmentHandler}
        >
          {(props) => (
            <Form className='mt-5 space-y-5'>
              {currentUserType === 'user' ? (
                <AcademiaFormSelect
                  name='client'
                  label='Select Client'
                  options={clientOptions}
                  defaultValue={props.values.client}
                  showSearchInput
                  isRequired
                />
              ) : null}

              <AcademiaTextAreaField
                name='additional_info'
                label='Additional Information'
                placeholder='Share additional information with our associate ahead of time here...'
              />

              <div className='grid grid-cols-2 gap-5'>
                <AcademiaButton
                  title={
                    currentUserType === 'user' && props.values.client.id === -1
                      ? 'Please, select a client first!'
                      : undefined
                  }
                  type='submit'
                  variant='success'
                  isLoading={props.isSubmitting}
                  isDisabled={
                    currentUserType === 'user' && props.values.client.id === -1
                  }
                >
                  Yes
                </AcademiaButton>

                <AcademiaButton
                  onClick={handleModalClose}
                  type='button'
                  variant='danger'
                >
                  No
                </AcademiaButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </ParentModal>
  );
};

export default SlotBookingConsentModal;
